/* Single client state store.
 *
 * ARCHITECTURE.md AD-08: 2D and 3D are two renderers over ONE store. Selection,
 * camera focus, time, and filters are store properties, never per-view properties.
 * Two scene graphs guarantee divergence; this is the mechanism that prevents it.
 *
 * AD-02: clients send intents, never state. Every mutation here is a local *view*
 * concern (what am I looking at). Simulation mutations would go out as intents to
 * the Gateway and come back as authoritative deltas.
 */
import { create } from 'zustand'
import type { ComponentState, LogEntry, OrbisContact, OrbisEntity } from '../data/types'
import { MAP_MODE, type BasemapId } from '../lib/basemaps'
import { availableBasemaps, type BundleManifest } from '../lib/bundle'
import type { ExclusionZone, RefPoint } from '../data/refpoints'
import { gateway } from '../lib/gateway'
import type { ConnectionState, GatewayEvent, ScenarioInfo, Snapshot, WhoAmI } from '../lib/gateway'
import { trackToContact } from '../lib/liveContacts'
import type { Mission } from '../data/missions'

export type ViewMode = '2d' | '3d'
export type Tool = 'select' | 'course' | 'measure' | 'annotate' | 'refpoint'
export type DetailTab =
  | 'status'
  | 'weapons'
  | 'sensors'
  | 'comms'
  | 'doctrine'
  | 'damage'
  | 'trace'

export type LayerId =
  | 'units'
  | 'contacts'
  | 'tracks'
  | 'sensorRanges'
  | 'weaponEnvelopes'
  | 'threatEnvelopes'
  | 'missions'
  | 'refPoints'
  | 'zones'
  | 'graticule'
  | 'bathymetry'
  | 'terrain'
  | 'truth'

export interface MeasureState {
  from: { lon: number; lat: number } | null
  to: { lon: number; lat: number } | null
}

/**
 * Plotted courses, keyed by entity id.
 *
 * A map rather than a single course because a plan is a property of the *unit*, not of the
 * plotting tool. The first version held one course and discarded it whenever the operator
 * selected another platform — which meant a two-aircraft plan was impossible to build, and
 * silently destroyed work. Keying by entity also makes the old cross-contamination bug
 * structurally impossible: waypoints can only ever be read under the id they were stored
 * under.
 */
export type Courses = Record<string, { lon: number; lat: number }[]>

export interface Selection {
  kind: 'entity' | 'contact'
  id: string
}

interface DeckState {
  // Time
  running: boolean
  compression: number
  elapsedSec: number

  /**
   * Whether the simulation state on screen comes from the real kernel or from the seeded
   * fixture. Surfaced in the status bar, never inferred: a demo that silently falls back to
   * mock data is indistinguishable from a working one until somebody asks whether the numbers
   * are real.
   */
  connection: ConnectionState
  /** Set once the first snapshot arrives. Until then the fixture values are what is drawn. */
  simTick: number
  /** Scenario epoch, from the kernel. The trace clock derives from this plus sim time — never
   *  from Date.now(), which would put real time beside simulated events. */
  epochIso: string
  /**
   * True until the first live snapshot replaces the seeded log.
   *
   * The fixture log is illustrative content stamped 08:22Z; a live run starts at its own epoch.
   * Showing both at once puts two unrelated timelines in one column, which is worse than showing
   * an empty log — an operator reading down it would infer an ordering that does not exist.
   */
  logIsFixture: boolean
  engineVersion: string | null
  /** Real content addresses from the kernel, replacing the fixture's hardcoded ones. */
  datasetRef: string | null
  scenarioDigest: string | null
  /** Run header, from the Gateway. Empty until connected — the title bar says so. */
  scenarioName: string
  /** Exercise length, from the scenario. A constant would make the scrubber lie for any
   *  scenario but the demo. */
  durationSec: number
  classification: string
  /** What the Gateway says this client may do. Asked on connect, never assumed — a UI that hides
   *  a button it was never told about will show it again after the next refactor. */
  me: WhoAmI | null
  seed: string | null
  /** Ticks the server's pacer could not fit into its last wake-up. Non-zero means the sim is
   *  running slower than the compression factor claims. */
  behindTicks: number

  // View
  viewMode: ViewMode
  /** Basemap selection — see lib/basemaps.ts */
  basemap: BasemapId
  /** 3D terrain relief. Independent of projection: usable in 2D and on the globe. */
  terrain3d: boolean
  terrainExaggeration: number
  tool: Tool
  layers: Record<LayerId, boolean>
  symbolStandard: '2525E' | 'APP6E'

  // Panels
  selection: Selection | null
  detailTab: DetailTab
  logOpen: boolean
  layersOpen: boolean
  paletteOpen: boolean

  /** Measure tool (FR-UI-06). Geodesic range + bearing between two picks. */
  measure: MeasureState
  /** Plotted courses per entity (FR-UI-06, UI-UX §5.2). */
  courses: Courses
  /**
   * The unit whose course a click extends when nothing is selected.
   *
   * Without this, deselecting mid-plot would leave clicks with no target and the operator
   * would silently draw nothing.
   */
  courseFocus: string | null
  /**
   * entity id -> sequence number the Gateway returned for the last course order we sent.
   * Compared against the snapshot's `courseSeq` so the client only reconciles against orders
   * the server has actually applied.
   */
  courseAck: Record<string, number>
  /** Guided tour — beat index, or null when not running. */
  demoStep: number | null
  /** Hands-off mode. The commonest demo failure is a presenter narrating while fumbling for the
   *  next click, so the tour can advance itself on each beat's own dwell time. */
  demoAutoplay: boolean

  // Data (would arrive as WS deltas from the Gateway)
  entities: OrbisEntity[]
  contacts: OrbisContact[]
  log: LogEntry[]
  refPoints: RefPoint[]
  zones: ExclusionZone[]
  missions: Mission[]
  missionsOpen: boolean
  selectedMissionId: string | null
  orbatOpen: boolean
  lossesOpen: boolean
  /** Camera request. MapCanvas owns the map, so panels ask via state rather than
   *  passing the instance around. Cleared once consumed. */
  focusTarget: { lon: number; lat: number; zoom?: number } | null

  // Actions
  toggleRunning: () => void
  /** Set the clock explicitly. The guided tour needs "make it run" rather than "flip it",
   *  because a toggle applied to unknown state gives the opposite of what the beat intended. */
  setRunning: (running: boolean) => void
  setCompression: (x: number) => void
  /** Restart the scenario from tick zero. EXCON-only, and enforced by the Gateway. */
  resetRun: () => void
  /** Open exactly one side panel, closing the others. A tour must not accumulate clutter. */
  setPanel: (p: 'none' | 'layers' | 'missions' | 'orbat' | 'losses' | 'analysis') => void
  /** Apply an authoritative snapshot from the kernel. */
  applySnapshot: (s: Snapshot) => void
  setConnection: (c: ConnectionState) => void
  appendEvents: (e: GatewayEvent[]) => void
  /** Apply the scenario description — dataset-derived class names, SIDCs and DIS tuples. */
  applyScenario: (s: ScenarioInfo) => void
  setMe: (me: WhoAmI | null) => void
  tick: (dt: number) => void
  setViewMode: (m: ViewMode) => void
  setBasemap: (b: BasemapId) => void
  toggleTerrain3d: () => void
  setTerrainExaggeration: (x: number) => void
  setTool: (t: Tool) => void
  toggleLayer: (l: LayerId) => void
  setSymbolStandard: (s: '2525E' | 'APP6E') => void
  select: (s: Selection | null) => void
  setDetailTab: (t: DetailTab) => void
  toggleLog: () => void
  toggleLayersPanel: () => void
  setPaletteOpen: (open: boolean) => void
  measurePick: (p: { lon: number; lat: number }) => void
  measureClear: () => void
  courseAdd: (entityId: string, p: { lon: number; lat: number }) => void
  courseUndo: (entityId: string) => void
  /** Clears one unit's course, or every course when called with no id. */
  courseClear: (entityId?: string) => void
  refPointAdd: (p: { lon: number; lat: number }) => void
  refPointMove: (id: string, p: { lon: number; lat: number }) => void
  refPointRename: (id: string, name: string) => void
  refPointRemove: (id: string) => void
  toggleOrbat: () => void
  toggleLosses: () => void
  /** Live DIS publication status. Diagnostics, not part of the picture. */
  interopOpen: boolean
  toggleInterop: () => void
  /** The scenario library — list and run. Authoring stays in the ORBAT builder. */
  scenarioOpen: boolean
  toggleScenario: () => void
  /** Range batch results. Read-only, and EXCON/analyst-only server-side — a batch reports every
   *  side's outcome over hundreds of runs of the exercise being fought. */
  analysisOpen: boolean
  toggleAnalysis: () => void
  /** The staged Atlas bundle this deployment serves, or null when there is none. Held in the
   *  store so the map can rebuild its style the moment the manifest arrives. */
  bundle: BundleManifest | null
  setBundle: (b: BundleManifest) => void
  focusOn: (p: { lon: number; lat: number; zoom?: number }) => void
  clearFocus: () => void
  toggleMissions: () => void
  selectMission: (id: string) => void
  missionSetOnStation: (id: string, n: number) => void
  missionToggleAssigned: (missionId: string, entityId: string) => void
  setDemoStep: (i: number | null) => void
  setDemoAutoplay: (on: boolean) => void
}

/**
 * Simulated wall-clock time as HH:MM:SS.
 *
 * Derived from the scenario epoch plus the kernel's integer nanoseconds — never from
 * `Date.now()`. The client displaying real time next to simulated events is how an operator
 * ends up misreading an exercise timeline.
 */
function simClock(epochIso: string, simTimeNs: number | undefined): string {
  const ms = Date.parse(epochIso) + (simTimeNs ?? 0) / 1e6
  // `toISOString` THROWS on a non-finite value rather than returning "Invalid Date". Inside a
  // store update that throw propagates into the socket handler, and it is how a whole batch of
  // events silently went missing once already.
  return Number.isFinite(ms) ? new Date(ms).toISOString().slice(11, 19) : '--:--:--'
}

/** Human text for an event. Kept in the client because wording is presentation. */
function describeEvent(e: GatewayEvent): string {
  const payload = e.payload ?? {}
  if (e.kind === 'order.issued') {
    const kind = String(payload.order_kind ?? 'order')
    if (payload.accepted === false) {
      return `Order refused — ${kind} (${String(payload.reason ?? 'no reason given')})`
    }
    if (kind === 'course') {
      const n = Number(payload.waypoint_count ?? 0)
      return `Course accepted — ${n} waypoint${n === 1 ? '' : 's'}, effective tick ${e.tick}`
    }
    if (kind === 'course_clear') return `Course cleared — effective tick ${e.tick}`
    return `${kind} accepted — effective tick ${e.tick}`
  }
  if (e.kind === 'run.started') return 'Run started — deterministic kernel engaged'
  return e.kind
}

/**
 * Send a course order and record the sequence the Gateway assigns it.
 *
 * The acknowledgement is what makes reconciliation safe: until the snapshot reports this same
 * sequence, the client knows the server is still flying an older plan (or none) and must leave
 * the drawn course alone.
 */
async function sendCourse(entityId: string, waypoints: { lon: number; lat: number }[]) {
  const res = await gateway.setCourse(entityId, waypoints)
  if (!res || !res.ok) return
  try {
    const { sequence } = (await res.json()) as { sequence: number }
    useDeck.setState((s) => ({ courseAck: { ...s.courseAck, [entityId]: sequence } }))
  } catch {
    // No sequence means no acknowledgement, so reconciliation simply stays off for this unit —
    // the drawn course is left as the operator entered it, which is the safe direction.
  }
}

export const useDeck = create<DeckState>((set) => ({
  // Starts paused, matching the server. A simulation already running when the operator arrives
  // gives them no chance to read the initial state before it changes.
  running: false,
  compression: 1,
  elapsedSec: 0,

  connection: 'connecting',
  simTick: 0,
  epochIso: '1970-01-01T00:00:00Z',
  logIsFixture: true,
  engineVersion: null,
  datasetRef: null,
  scenarioDigest: null,
  scenarioName: '',
  durationSec: 43200,
  classification: '',
  me: null,
  seed: null,
  behindTicks: 0,

  viewMode: '2d',
  // In bundle (air-gapped) mode there are no upstream tiles, so the offline
  // fixture is the only honest default.
  basemap: MAP_MODE === 'bundle' ? 'offline' : 'tactical',
  terrain3d: false,
  terrainExaggeration: 1.4,
  tool: 'select',
  layers: {
    units: true,
    contacts: true,
    tracks: true,
    sensorRanges: true,
    weaponEnvelopes: false,
    threatEnvelopes: true,
    missions: true,
    refPoints: true,
    zones: true,
    graticule: true,
    bathymetry: true,
    terrain: true,
    truth: false,
  },
  symbolStandard: '2525E',

  selection: { kind: 'entity', id: '018f2c9e-0001' },
  detailTab: 'trace',
  logOpen: true,
  layersOpen: false,
  paletteOpen: false,
  measure: { from: null, to: null },
  courses: {},
  courseFocus: null,
  courseAck: {},
  demoStep: null,
  demoAutoplay: false,

  // EMPTY until the Gateway answers. There is no fixture to fall back to any more, and that is
  // the point of week 6: an empty map with an OFFLINE badge is honest, whereas a populated one
  // that turns out to be scripted is the failure this whole exercise exists to avoid.
  entities: [],
  contacts: [],
  log: [],
  // Empty until the Gateway reports the exercise's plan. Seeding these from a fixture is what
  // made a scenario with no missions show the Baltic's — the client asserting a plan nobody
  // authored, in the one panel an operator would act on.
  refPoints: [],
  zones: [],
  missions: [],
  missionsOpen: false,
  selectedMissionId: null,
  orbatOpen: false,
  lossesOpen: false,
  interopOpen: false,
  scenarioOpen: false,
  analysisOpen: false,
  bundle: null,
  focusTarget: null,

  // Control actions are OPTIMISTIC in the UI and AUTHORITATIVE on the server: the button
  // responds immediately, and the next snapshot overwrites whatever the client assumed. Waiting
  // for the round trip would make the transport's latency look like sluggish software; ignoring
  // the server's answer would let the two drift apart silently.
  toggleRunning: () => useDeck.getState().setRunning(!useDeck.getState().running),

  setRunning: (running) => {
    set({ running })
    void (running ? gateway.play() : gateway.pause())
  },

  resetRun: () => {
    // Clears the CLIENT-side plot state as well as asking the server to rewind.
    //
    // Courses are drawn from `courses`, which is client state acknowledged against the snapshot's
    // `courseSeq`. After a reset the server has no orders and its seq counters restart, so a
    // client that kept its plotted paths would draw routes for a run that no longer has them —
    // and `courseAck` would never match again, so they would never be cleaned up either.
    //
    // Not routed through `courseClear()`: that POSTS an empty course per entity, and sending
    // orders to a simulation that is being rewound is both pointless and a race.
    set({
      courses: {},
      courseAck: {},
      courseFocus: null,
      running: false,
      elapsedSec: 0,
      simTick: 0,
      // Selection and the detail panel go too: a trace panel left open after a rewind is showing
      // an engagement that has not happened yet in the run now on screen.
      selection: null,
      // The log is NOT cleared. It is the record of what happened, and an operator who has just
      // rewound is usually rewinding *because* of something in it.
    })
    // Pause FIRST, then reset. The server's reset deliberately preserves whether the clock was
    // running (runner.py `was_running`), so a reset while playing restarts the scenario and
    // immediately charges forward — the operator never sees the laydown they just asked to go
    // back to, and has no chance to re-plot before it moves.
    //
    // Sequenced rather than fired together: two concurrent POSTs can arrive in either order, and
    // pausing *after* the reset would leave the clock stopped at a tick or two past zero.
    //
    // The optimistic `running: false` above therefore matches what the server will report, rather
    // than contradicting it and flipping the button back on the next snapshot.
    void (async () => {
      try {
        await gateway.pause()
        await gateway.reset()
      } catch (err) {
        // The next snapshot is authoritative and will correct the optimistic state above, so the
        // UI self-heals. Logged rather than swallowed: a reset that silently did nothing is
        // indistinguishable from one that worked on a paused run.
        console.error('reset failed', err)
      }
    })()
  },

  setPanel: (panel) =>
    set({
      layersOpen: panel === 'layers',
      missionsOpen: panel === 'missions',
      orbatOpen: panel === 'orbat',
      lossesOpen: panel === 'losses',
      // Included so a tour beat that opens the analysis panel closes it again on the next beat.
      // A panel left open behind the one that follows it is the clutter `setPanel` exists to
      // prevent, and it is the presenter who has to notice.
      analysisOpen: panel === 'analysis',
    }),
  setCompression: (compression) => {
    set({ compression })
    void gateway.setCompression(compression)
  },

  // Local clock, used ONLY while offline. When a snapshot is arriving, sim time comes from the
  // kernel — the client must never be a second source of truth for time.
  tick: (dt) =>
    set((s) =>
      s.connection === 'live' ? {} : { elapsedSec: s.elapsedSec + dt * s.compression },
    ),

  applySnapshot: (snap) =>
    set((s) => {
      // ── The world may have been REPLACED under us ──────────────────────────
      //
      // A scenario activated from the ORBAT builder swaps the entire world at tick 0. Everything
      // the client holds *per entity id* then refers to units that no longer exist: plotted
      // courses, the selection, mission assignments, the acknowledged-order map. None of it
      // errors — a course keyed to a missing id simply draws a plan for nobody, which reads as a
      // rendering bug rather than as stale state.
      //
      // The digest is the signal, not the name. Two scenarios can share a name; the digest changes
      // if and only if the document did. It is deliberately NOT triggered on the first snapshot
      // (`scenarioDigest === null`), which would wipe a course plotted while connecting.
      const replaced =
        s.scenarioDigest !== null &&
        snap.scenarioDigest !== undefined &&
        snap.scenarioDigest !== s.scenarioDigest
      const cleaned = replaced
        ? {
            courses: {} as Courses,
            courseAck: {} as Record<string, number>,
            courseFocus: null,
            selection: null,
            measure: { from: null, to: null } as MeasureState,
            // Mission assignments name entity ids. Kept as missions (they are doctrine, not
            // units) but unassigned, because an assignment to a unit that is not in this
            // scenario would make the feasibility verdict quietly meaningless.
            missions: s.missions.map((m) => ({ ...m, assigned: [] })),
            selectedMissionId: null,
            log: [
              {
                id: `scenario-${snap.scenarioDigest}`,
                simTime: simClock(snap.epochIso, snap.simTimeNs),
                severity: 'warn' as const,
                source: 'EXCON',
                message: `Scenario changed — now running ${snap.scenarioName || 'a new scenario'} (${(snap.scenarioDigest ?? '').slice(0, 12)}). Plotted courses, selection and mission assignments were cleared.`,
              },
            ],
          }
        : null
      // Entities are BUILT from the snapshot, not patched into a fixture. Every field the Deck
      // renders now originates in the kernel or in the digest-pinned dataset — there is no local
      // copy left to drift out of step, which was the point of week 6.
      const entities: OrbisEntity[] = snap.entities.map((u) => ({
        id: u.id,
        designation: u.designation,
        className: u.classRef,
        side: (u.side === 'red' ? 'red' : 'blue') as OrbisEntity['side'],
        affiliation: u.side === 'red' ? '6' : '3',
        domain: (u.domain ?? 'air') as OrbisEntity['domain'],
        sidc: u.sidc ?? '',
        lat: u.lat,
        lon: u.lon,
        courseDeg: u.courseDeg,
        speedKt: u.speedKt,
        altitudeFt: u.altitudeFt > 1 ? u.altitudeFt : undefined,
        fuelFraction: u.fuelFraction,
        enduranceHrs: u.enduranceHrs,
        // Readiness is a DISPLAY summary of facts the kernel reports, not a field it owns.
        // Deriving it here keeps a wording judgement in the client, where it belongs.
        readiness: u.bingo
          ? 'Bingo fuel'
          : (u.components ?? []).some((c) => c.health === 'destroyed')
            ? 'Damaged'
            : u.speedKt > 0
              ? 'On station'
              : 'Operational',
        mission: u.mission || undefined,
        emcon: u.emcon ?? { radar: 'active', comms: 'open' },
        provenance: u.provenance,
        dis: u.dis,
        components: (u.components ?? []).map((c) => ({
          id: c.id,
          kind: c.kind as ComponentState['kind'],
          name: c.name,
          health: c.health as ComponentState['health'],
          detail: c.detail,
          rangeNm: c.rangeNm,
          arcHalfWidthDeg: c.arcHalfWidthDeg,
          arcOffsetDeg: c.arcOffsetDeg,
          sensorKind: c.sensorKind as ComponentState['sensorKind'],
          weaponRangeNm: c.weaponRangeNm,
        })),
      }))

      const contacts = snap.contacts ? snap.contacts.map(trackToContact) : []

      // Core CONSUMES waypoints as it reaches them, so the drawn plan shrinks with the flown one.
      // Trimmed by count against an ACKNOWLEDGED order — `waypointsRemaining` alone is ambiguous,
      // being 0 both when a plan is finished and when the order has not been ingested yet.
      let courses = s.courses
      for (const u of snap.entities) {
        const drawn = courses[u.id]
        if (!drawn) continue
        if (u.courseSeq === 0 || u.courseSeq !== s.courseAck[u.id]) continue
        if (drawn.length <= u.waypointsRemaining) continue
        const remaining = drawn.slice(drawn.length - u.waypointsRemaining)
        courses = remaining.length
          ? { ...courses, [u.id]: remaining }
          : Object.fromEntries(Object.entries(courses).filter(([k]) => k !== u.id))
      }

      const log = s.logIsFixture
        ? [
            {
              id: 'live-connected',
              simTime: simClock(snap.epochIso, snap.simTimeNs),
              severity: 'info' as const,
              source: 'EXCON',
              message: `Connected to ORBIS Core ${snap.engineVersion} — dataset ${snap.datasetRef ?? '?'}, seed ${snap.seed}. Every value on screen is computed.`,
            },
          ]
        : s.log

      return {
        entities,
        contacts,
        courses,
        log,
        logIsFixture: false,
        // Applied LAST so it overrides the per-entity state computed above — the course trimming
        // in particular, which would otherwise re-derive courses for a world that is gone.
        ...(cleaned ?? {}),
        scenarioName: snap.scenarioName || s.scenarioName,
        running: snap.running,
        compression: snap.compression,
        simTick: snap.tick,
        epochIso: snap.epochIso,
        elapsedSec: snap.simTimeNs / 1e9,
        engineVersion: snap.engineVersion,
        datasetRef: snap.datasetRef ?? null,
        scenarioDigest: snap.scenarioDigest ?? null,
        seed: snap.seed,
        behindTicks: snap.behindTicks,
      }
    }),

  applyScenario: (info) =>
    // The run's header, plus its PLAN. Entity identity rides every snapshot, so this supplies
    // what is true of the run rather than of a unit.
    //
    // The plan REPLACES whatever is held rather than merging into it. A merge would leave the
    // previous exercise's patrol boxes on the map after a scenario change, labelled as this
    // exercise's — and the reference points are exactly what an operator would use to describe a
    // position to somebody else.
    set((s) => {
      const missions = info.missions ?? []
      return {
        scenarioName: info.name,
        classification: info.classification ?? '',
        epochIso: info.epochIso,
        datasetRef: info.dataset ?? null,
        scenarioDigest: info.scenarioDigest ?? null,
        refPoints: info.refPoints ?? [],
        zones: info.zones ?? [],
        missions,
        // Keep the selection only if the new plan still contains it.
        selectedMissionId:
          missions.some((m) => m.id === s.selectedMissionId)
            ? s.selectedMissionId
            : (missions[0]?.id ?? null),
      }
    }),

  setConnection: (connection) => set({ connection }),
  setMe: (me) => set({ me }),

  appendEvents: (incoming) =>
    set((s) => {
      // Only kinds an operator acts on. Motion is thousands of events per second at ×300 and
      // would bury everything that matters.
      const shown = incoming.filter((e) => e.kind === 'order.issued' || e.kind === 'run.started')
      if (!shown.length) return {}
      const seen = new Set(s.log.map((l) => l.id))
      const known = new Map(s.entities.map((e) => [e.id, e.designation]))
      const fresh = shown
        .filter((e) => !seen.has(`ev-${e.seq}`))
        .map((e) => ({
          id: `ev-${e.seq}`,
          simTime: simClock(useDeck.getState().epochIso, e.t),
          severity: (e.payload?.accepted === false ? 'warn' : 'info') as LogEntry['severity'],
          source: known.get(e.subject ?? '') ?? e.subject ?? 'EXCON',
          message: describeEvent(e),
        }))
      if (!fresh.length) return {}
      // Newest first, matching the dock's existing order, and bounded — an unbounded log is a
      // memory leak in a long exercise.
      return { log: [...fresh.reverse(), ...s.log].slice(0, 200) }
    }),
  setViewMode: (viewMode) => set({ viewMode }),
  setBasemap: (basemap) => set({ basemap }),
  toggleTerrain3d: () => set((s) => ({ terrain3d: !s.terrain3d })),
  setTerrainExaggeration: (terrainExaggeration) => set({ terrainExaggeration }),
  setTool: (tool) => set({ tool }),
  toggleLayer: (l) => set((s) => ({ layers: { ...s.layers, [l]: !s.layers[l] } })),
  setSymbolStandard: (symbolStandard) => set({ symbolStandard }),
  select: (selection) => set({ selection }),
  setDetailTab: (detailTab) => set({ detailTab }),
  toggleLog: () => set((s) => ({ logOpen: !s.logOpen })),
  toggleLayersPanel: () => set((s) => ({ layersOpen: !s.layersOpen })),
  setPaletteOpen: (paletteOpen) => set({ paletteOpen }),

  // First pick sets `from`; second sets `to`; a third starts over. Simpler than a
  // modal state machine and matches what operators expect from a ruler.
  measurePick: (p) =>
    set((s) => {
      if (!s.measure.from || s.measure.to) return { measure: { from: p, to: null } }
      return { measure: { from: s.measure.from, to: p } }
    }),
  measureClear: () => set({ measure: { from: null, to: null } }),

  // Courses are stored per entity, so plotting for one unit never touches another's plan.
  //
  // Each change is ALSO sent to the Gateway as an order (AD-02: clients send intents). The plot
  // is the order — there is no separate "execute" step, so a unit turns onto the first leg as
  // soon as it is drawn, which is what makes the causal link visible.
  courseAdd: (entityId, p) =>
    set((s) => {
      const waypoints = [...(s.courses[entityId] ?? []), p]
      void sendCourse(entityId, waypoints)
      return { courses: { ...s.courses, [entityId]: waypoints }, courseFocus: entityId }
    }),
  courseUndo: (entityId) =>
    set((s) => {
      const remaining = (s.courses[entityId] ?? []).slice(0, -1)
      void sendCourse(entityId, remaining)
      if (remaining.length) return { courses: { ...s.courses, [entityId]: remaining } }
      // Drop the key rather than leave an empty array: a unit with no waypoints has no
      // course, and an empty entry would still count as one in every consumer.
      const { [entityId]: _dropped, ...rest } = s.courses
      return { courses: rest }
    }),
  courseClear: (entityId) =>
    set((s) => {
      if (entityId === undefined) {
        for (const id of Object.keys(s.courses)) void sendCourse(id, [])
        return { courses: {}, courseFocus: null, courseAck: {} }
      }
      void sendCourse(entityId, [])
      const { [entityId]: _dropped, ...rest } = s.courses
      return { courses: rest, courseFocus: s.courseFocus === entityId ? null : s.courseFocus }
    }),

  refPointAdd: (p) =>
    set((s) => {
      // Sequential phonetic names, skipping any already taken, so adding a point
      // never silently produces a duplicate name a mission might then bind to.
      const PHONETIC = [
        'ALPHA', 'BRAVO', 'CHARLIE', 'DELTA', 'ECHO', 'FOXTROT', 'GOLF', 'HOTEL',
        'INDIA', 'JULIETT', 'KILO', 'LIMA', 'MIKE', 'NOVEMBER', 'OSCAR', 'PAPA',
      ]
      const taken = new Set(s.refPoints.map((r) => r.name))
      const next = PHONETIC.map((n) => `RP-${n}`).find((n) => !taken.has(n))
      const name = next ?? `RP-${s.refPoints.length + 1}`
      return {
        refPoints: [
          ...s.refPoints,
          { id: `rp-${name.toLowerCase()}-${s.refPoints.length}`, name, lon: p.lon, lat: p.lat, side: 'blue' },
        ],
      }
    }),
  refPointMove: (id, p) =>
    set((s) => ({
      refPoints: s.refPoints.map((r) => (r.id === id ? { ...r, lon: p.lon, lat: p.lat } : r)),
    })),
  refPointRename: (id, name) =>
    set((s) => ({ refPoints: s.refPoints.map((r) => (r.id === id ? { ...r, name } : r)) })),
  refPointRemove: (id) => set((s) => ({ refPoints: s.refPoints.filter((r) => r.id !== id) })),

  toggleOrbat: () => set((s) => ({ orbatOpen: !s.orbatOpen })),
  toggleLosses: () => set((s) => ({ lossesOpen: !s.lossesOpen })),
  toggleInterop: () => set((s) => ({ interopOpen: !s.interopOpen })),
  toggleScenario: () => set((s) => ({ scenarioOpen: !s.scenarioOpen })),
  toggleAnalysis: () => set((s) => ({ analysisOpen: !s.analysisOpen })),
  setBundle: (bundle) =>
    set((s) => {
      // The store's default in bundle mode is the placeholder style, because at module-load time
      // nothing knows whether a bundle exists. Once one arrives, move to a basemap it can actually
      // draw — otherwise a deployment with 80 MB of staged tiles renders inline coastlines and
      // looks exactly like a deployment with none.
      const available = availableBasemaps(bundle)
      const useIt = s.basemap === 'offline' && available.length > 0
      return { bundle, basemap: useIt ? (available[0] as BasemapId) : s.basemap }
    }),
  focusOn: (focusTarget) => set({ focusTarget }),
  clearFocus: () => set({ focusTarget: null }),
  toggleMissions: () => set((s) => ({ missionsOpen: !s.missionsOpen })),
  selectMission: (selectedMissionId) => set({ selectedMissionId }),
  missionSetOnStation: (id, n) =>
    set((s) => ({
      missions: s.missions.map((m) => (m.id === id ? { ...m, onStation: Math.max(1, n) } : m)),
    })),
  missionToggleAssigned: (missionId, entityId) =>
    set((s) => ({
      missions: s.missions.map((m) =>
        m.id === missionId
          ? {
              ...m,
              assigned: m.assigned.includes(entityId)
                ? m.assigned.filter((x) => x !== entityId)
                : [...m.assigned, entityId],
            }
          : m,
      ),
    })),
  setDemoStep: (demoStep) => set({ demoStep }),
  setDemoAutoplay: (demoAutoplay) => set({ demoAutoplay }),
}))

// ─── Derived selectors ───────────────────────────────────────────────────────

export function useSelectedEntity(): OrbisEntity | null {
  return useDeck((s) =>
    s.selection?.kind === 'entity'
      ? (s.entities.find((e) => e.id === s.selection!.id) ?? null)
      : null,
  )
}

export function useSelectedContact(): OrbisContact | null {
  return useDeck((s) =>
    s.selection?.kind === 'contact'
      ? (s.contacts.find((c) => c.id === s.selection!.id) ?? null)
      : null,
  )
}
